import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { NumberOnlyDirective } from '../../directives/number-only.directive';
import { GbTheme, THEME_SIGNAL } from '../../interfaces/theme';

export interface OtpSetting {
  length: number;
  timer: number;
  timerType: number;
  btnText: string;
  btnLabel?: string;
  btnClass?: string;
  wrapperClass?: string;
  inputClass?: string;
  wrapperStyles?: Record<string, string>;
  inputStyles?: Record<string, string>;
  numbersOnly?: boolean;
}

export const REQUESTED_NEW_OTP = -2;

export const TIMER_RESET_COMPLETE = -1;

export type OtpValueChangeType =
  | string
  | typeof REQUESTED_NEW_OTP
  | typeof TIMER_RESET_COMPLETE
  | undefined;

@Component({
  selector: 'app-otp-input',
  standalone: true,
  imports: [CommonModule, NumberOnlyDirective, TranslatePipe],
  templateUrl: './otp-input.component.html',
  styleUrl: './otp-input.component.scss',
})
export class OtpInputComponent implements OnInit, OnDestroy {
  @Input() setting: OtpSetting = {
    length: 6,
    timer: 30,
    timerType: 0,
    btnText: 'otp_input.btn_text',
  };
  @Input() hasError = false;
  @Output() valueChange = new EventEmitter<OtpValueChangeType>();

  // Modern signal-based state management
  otpValues = signal<string[]>([]);
  focusedIndex = signal<number>(-1);
  counter = signal<number>(0);

  public theme = inject(THEME_SIGNAL);

  public GbTheme = GbTheme;

  // Computed values
  isTimerActive = computed(() => this.counter() > 0);
  isComplete = computed(() => this.otpValues().join('').length === this.setting.length);
  otpString = computed(() => this.otpValues().join(''));

  private timerInterval?: number;

  constructor() {
    // Initialize OTP array
    this.otpValues.set(new Array(this.setting.length).fill(''));

    // Effect to handle timer completion
    effect(() => {
      if (this.counter() === 0 && this.isTimerActive()) {
        this.valueChange.emit(TIMER_RESET_COMPLETE);
      }
    });
  }

  ngOnInit(): void {
    this.startTimer();
  }

  ngOnDestroy(): void {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
  }

  // Simplified input handling
  onInputChange(event: Event, index: number): void {
    const target = event.target as HTMLInputElement;
    const value = target.value;

    const newValues = [...this.otpValues()];
    newValues[index] = value.slice(-1); // Take only last character
    this.otpValues.set(newValues);

    // Emit current OTP value on every input change
    const currentOtp = this.otpString();
    this.valueChange.emit(currentOtp);

    // Auto-focus next input
    if (value && index < this.setting.length - 1) {
      this.focusInput(index + 1);
    }
  }

  onKeyDown(event: KeyboardEvent, index: number): void {
    if (event.key === 'Backspace' && !this.otpValues()[index] && index > 0) {
      // Move to previous input on backspace
      this.focusInput(index - 1);
    } else if (event.key === 'ArrowLeft' && index > 0) {
      this.focusInput(index - 1);
    } else if (event.key === 'ArrowRight' && index < this.setting.length - 1) {
      this.focusInput(index + 1);
    }
  }

  onFocus(index: number): void {
    this.focusedIndex.set(index);
  }
  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pastedText = event.clipboardData?.getData('text').replaceAll(/\s+/g, '') || '';

    if (!/^\d{6}$/.test(pastedText)) {
      this.resetOtp();
      return;
    }

    const newValues = pastedText.split('').slice(0, this.setting.length);
    this.otpValues.set(newValues);

    // Emit current OTP value after paste
    const currentOtp = this.otpString();
    this.valueChange.emit(currentOtp);

    // Focus last input
    this.focusInput(this.setting.length - 1);
  }

  private focusInput(index: number): void {
    this.focusedIndex.set(index);
    setTimeout(() => {
      const input = document.getElementById(`otp-${index}`) as HTMLInputElement;
      input?.focus();
    }, 0);
  }

  public resetOtp(emitChange = false): void {
    this.otpValues.set(new Array(this.setting.length).fill(''));
    this.focusInput(0);
    if (emitChange) {
      this.valueChange.emit('');
    }
  }

  resendOtp(): void {
    this.valueChange.emit(REQUESTED_NEW_OTP);
  }

  private startTimer(): void {
    this.counter.set(this.setting.timer);

    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }

    this.timerInterval = globalThis.setInterval(() => {
      const currentValue = this.counter();
      if (currentValue > 0) {
        this.counter.set(currentValue - 1);
      } else {
        clearInterval(this.timerInterval);
      }
    }, 1000) as unknown as number;
  }

  formatSecsToMins(time: number): string {
    const hrs = Math.trunc(time / 3600);
    const mins = Math.trunc((time % 3600) / 60);
    const secs = Math.trunc(time) % 60;

    let ret = '';
    if (hrs > 0) {
      ret += '' + hrs + ':' + (mins < 10 ? '0' : '');
    }
    ret += '' + mins + ':' + (secs < 10 ? '0' : '');
    ret += '' + secs;
    return ret;
  }

  // Getter for template
  get otpArray(): string[] {
    return this.otpValues();
  }
}
